import { Popover } from "@headlessui/react"
import { format, getDay, isSameDay, isWeekend, parseISO } from "date-fns"
import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { useMutation } from "react-query"

import Form from "src/components/Form"
import Table from "src/components/Table"

import { bulkUpdateCalendarDay } from "src/api/TransientRates"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import BulkDeleteModal from "./BulkDeleteModal"
import BulkEditModal from "./BulkEditModal"
import MultiSelectDropdown from "./MultiSelectDropdown"
import RateStrategyPicker from "./RateStrategyPicker"

const initialFilters = {
  dayOfWeek: [
    { id: 0, name: "Sunday" },
    { id: 1, name: "Monday" },
    { id: 2, name: "Tuesday" },
    { id: 3, name: "Wednesday" },
    { id: 4, name: "Thursday" },
    { id: 5, name: "Friday" },
    { id: 6, name: "Saturday" },
  ],
  mapped: [
    { id: "mapped", name: "Mapped" },
    { id: "unmapped", name: "Unmapped" },
  ],
}

function buildDaysMap(allDays) {
  return allDays.reduce((acc, item) => {
    acc[item.day.date] = item
    return acc
  }, {})
}

function updateDayStrategy(daysMap, date, selectedStrategy) {
  return {
    ...daysMap,
    [date]: {
      ...daysMap[date],
      day: {
        ...daysMap[date].day,
        rate_strategy_id: selectedStrategy.value,
      },
      strategy: {
        ...daysMap[date].strategy,
        existingStrategy: {
          value: selectedStrategy.value,
          label: selectedStrategy.label,
        },
      },
    },
  }
}

const CalendarDaysTable = ({ data, bulkUpdateUrl, timeframe }) => {
  const [daysMap, setDaysMap] = useState(buildDaysMap(data))

  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState(
    initialFilters.dayOfWeek
  )
  const [selectedStartDate, setSelectedStartDate] = useState(
    parseISO(data[0].day.date)
  )
  const [selectedEndDate, setSelectedEndDate] = useState(
    parseISO(data[data.length - 1].day.date)
  )
  const [selectedMappingStates, setSelectedMappingStates] = useState(
    initialFilters.mapped
  )

  const [showBulkEditModal, setShowBulkEditModal] = useState(false)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const visibleDays = useMemo(() => {
    return Object.values(daysMap)
      .filter((dayConfiguration) => {
        const itemDay = getDay(parseISO(dayConfiguration.day.date))
        return selectedDaysOfWeek.map((i) => i.id).includes(itemDay)
      })
      .filter((dayConfiguration) => {
        const {
          day: { rate_strategy_id: strategyId },
        } = dayConfiguration
        const itemMapping = strategyId === null ? "unmapped" : "mapped"
        return selectedMappingStates
          .map((mappingState) => mappingState.id)
          .includes(itemMapping)
      })
      .filter((dayConfiguration) => {
        const itemStartDate = parseISO(dayConfiguration.day.date)
        return itemStartDate >= selectedStartDate
      })
      .filter((dayConfiguration) => {
        const itemEndDate = parseISO(dayConfiguration.day.date)
        return itemEndDate <= selectedEndDate
      })
  }, [
    daysMap,
    selectedDaysOfWeek,
    selectedMappingStates,
    selectedStartDate,
    selectedEndDate,
  ])

  const unmappedCount = visibleDays.filter(
    (item) => item.day.rate_strategy_id === null
  ).length

  const showToast = useToast()
  const tracker = useTracker()

  const handleDayOfWeekChange = (returnedFilters) => {
    setSelectedDaysOfWeek(
      initialFilters.dayOfWeek.filter((day) => returnedFilters.includes(day.id))
    )
  }

  const handleMappingChange = (returnedFilters) => {
    setSelectedMappingStates(
      initialFilters.mapped.filter((mapping) =>
        returnedFilters.includes(mapping.id)
      )
    )
  }

  const handleDatePickerChange = (button, value) => {
    setSelectedStartDate(
      button === "strategyStartDate" ? value : selectedStartDate
    )
    setSelectedEndDate(button === "strategyEndDate" ? value : selectedEndDate)
  }

  const dateRangeModified = () => {
    return (
      !isSameDay(parseISO(data[0].day.date), selectedStartDate) ||
      !isSameDay(parseISO(data[data.length - 1].day.date), selectedEndDate)
    )
  }

  const { mutate } = useMutation({
    mutationFn: (data) => bulkUpdateCalendarDay({ data, bulkUpdateUrl }),
    onSuccess: (updates, variables) => {
      const isBulkEdit = variables[0]?.rate_strategy_id != null
      if (isBulkEdit) {
        tracker.trackEvent(
          "transient_rates_v2:calendar_bulk_update_completed",
          {
            season_id: firstVisibleDayStrategyOptions[0]?.seasonId,
            rate_strategy_id: variables[0]?.rate_strategy_id,
            days_updated_count: updates.length,
            timeframe,
            day_of_week_filter: selectedDaysOfWeek.map((d) => d.name),
          }
        )
      }

      setShowBulkEditModal(false)
      setShowBulkDeleteModal(false)
      setIsLoading(false)

      const updatesDayMap = buildDaysMap(updates)
      setDaysMap({ ...daysMap, ...updatesDayMap })

      showToast(
        `Updated ${visibleDays.length} Rate ${
          visibleDays.length === 1 ? "Strategy" : "Strategies"
        }`,
        { type: "success" }
      )
    },
    onError: (error) => {
      setShowBulkEditModal(false)
      setShowBulkDeleteModal(false)
      setIsLoading(false)
      showToast(error.message, { type: "error" })
    },
  })

  const onBulkActionsSelection = (value) => {
    if (visibleDays.length === 0) {
      showToast("No days visible to perform bulk action", { type: "error" })
      return
    }

    if (value === "edit") {
      setShowBulkEditModal(true)
    } else if (value === "delete") {
      setShowBulkDeleteModal(true)
    }
  }

  const handleBulkSelectChange = (strategy) => {
    setIsLoading(true)
    const params = visibleDays.map((item) => {
      return {
        calendar_date: item.day.date,
        rate_strategy_id: strategy?.value,
        timeframe,
      }
    })
    mutate(params)
  }

  const firstVisibleDayStrategyOptions =
    visibleDays[0]?.strategy?.strategyOptions ?? []

  const renderDateDropdown = () => (
    <Popover as="div" className="relative">
      <Popover.Button className="relative h-10 w-full cursor-default rounded border bg-white px-2 text-left outline-none focus:border-blue-600">
        <span>Date Range</span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <i
            className="fa-kit fa-angle-down absolute right-3 top-3 text-xs"
            aria-hidden="true"
          ></i>
          {dateRangeModified() && (
            <div className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-blue-600"></div>
          )}
        </span>
      </Popover.Button>
      <Popover.Panel className="absolute left-0 z-10 mt-2 w-72 origin-top-left rounded border bg-white p-4 shadow-lg">
        <div className="flex gap-4">
          <div>
            <Form.Label htmlFor="strategyStartDate">Start Date</Form.Label>
            <Form.DatePicker
              value={selectedStartDate}
              onChange={(value) => {
                handleDatePickerChange("strategyStartDate", value)
              }}
              id="startDate"
              minDate={parseISO(data[0].day.date)}
              maxDate={selectedEndDate}
              todayButton="Today"
            />
          </div>
          <div>
            <Form.Label htmlFor="strategyEndDate">End Date</Form.Label>
            <Form.DatePicker
              value={selectedEndDate}
              onChange={(value) => {
                handleDatePickerChange("strategyEndDate", value)
              }}
              id="endDate"
              minDate={selectedStartDate}
              maxDate={parseISO(data[data.length - 1].day.date)}
              todayButton="Today"
            />
          </div>
        </div>
      </Popover.Panel>
    </Popover>
  )

  return (
    <>
      <div className="mb-4 flex w-full flex-wrap items-center md:flex-nowrap md:space-x-4">
        <div className="w-full md:w-44">
          <MultiSelectDropdown
            label="Days"
            options={initialFilters.dayOfWeek}
            selected={selectedDaysOfWeek.map((d) => d.id)}
            setSelected={(filters) => handleDayOfWeekChange(filters)}
            showIndicator={
              !(
                selectedDaysOfWeek.length === initialFilters.dayOfWeek.length
              ) &&
              selectedDaysOfWeek
                .map((i) => i.id)
                .every((val) =>
                  initialFilters.dayOfWeek.map((i) => i.id).includes(val)
                )
            }
          />
        </div>
        <div className="w-full md:w-44">
          <MultiSelectDropdown
            label="Rate Strategy"
            options={initialFilters.mapped}
            selected={selectedMappingStates.map((d) => d.id)}
            setSelected={(filters) => handleMappingChange(filters)}
            showIndicator={
              !(
                selectedMappingStates.length === initialFilters.mapped.length
              ) &&
              selectedMappingStates
                .map((i) => i.id)
                .every((val) =>
                  initialFilters.mapped.map((i) => i.id).includes(val)
                )
            }
          />
        </div>
        <div className="w-full md:w-44">{renderDateDropdown()}</div>
        {unmappedCount > 0 && timeframe === "nightly" && (
          <div>
            <i className="fa-kit fa-exclamation-circle mr-2 text-red-600" />
            <span className="text-red-600">
              {unmappedCount} {unmappedCount === 1 ? "day needs" : "days need"}{" "}
              mapping
            </span>
          </div>
        )}
        <div className="grow md:flex md:justify-end">
          <div className="w-full md:w-44">
            <Form.Select.Custom
              id="bulk-select"
              onChange={onBulkActionsSelection}
              value={""}
              label="Bulk Actions"
            >
              <Form.Select.RichOption value="edit" hideCheck>
                <i className="fa-kit fa-link mr-2" />
                <span>Map Strategies</span>
              </Form.Select.RichOption>
              {timeframe !== "nightly" && (
                <Form.Select.RichOption value="delete" hideCheck>
                  <i className="fa-kit fa-sf-trashcan mr-2" />
                  <span>Delete Strategies</span>
                </Form.Select.RichOption>
              )}
            </Form.Select.Custom>
          </div>
        </div>
      </div>
      <Table>
        <Table.Head>
          <Table.Head.Row>
            <Table.Head.Cell columnWidth="30%">Date</Table.Head.Cell>
            <Table.Head.Cell>Rate Strategy</Table.Head.Cell>
          </Table.Head.Row>
        </Table.Head>
        <Table.Body>
          {visibleDays?.map((calendarDay) => (
            <Table.Row
              key={calendarDay.day.date}
              rowColor={
                isWeekend(parseISO(calendarDay.day.date)) ? "shaded" : "default"
              }
            >
              <Table.Cell>
                {format(parseISO(calendarDay.day.date), "E, LLL d, y")}
              </Table.Cell>
              <Table.Cell>
                <RateStrategyPicker
                  existingStrategy={calendarDay.strategy.existingStrategy}
                  strategyOptions={calendarDay.strategy.strategyOptions}
                  timeframe={timeframe}
                  updateUrl={calendarDay.strategy.updateUrl}
                  onChange={(selectedStrategy) => {
                    setDaysMap((prevDaysMap) =>
                      updateDayStrategy(
                        prevDaysMap,
                        calendarDay.day.date,
                        selectedStrategy
                      )
                    )
                  }}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {showBulkEditModal ? (
        <BulkEditModal
          onClose={() => setShowBulkEditModal(false)}
          isLoading={isLoading}
          setSelectedStrategy={handleBulkSelectChange}
          recordCount={visibleDays.length}
          strategyOptions={firstVisibleDayStrategyOptions}
          timeframe={timeframe}
        />
      ) : null}

      {showBulkDeleteModal ? (
        <BulkDeleteModal
          onClose={() => setShowBulkDeleteModal(false)}
          isLoading={isLoading}
          setSelectedStrategy={handleBulkSelectChange}
          recordCount={visibleDays.length}
          strategyOptions={firstVisibleDayStrategyOptions}
          timeframe={timeframe}
        />
      ) : null}
    </>
  )
}

CalendarDaysTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  bulkUpdateUrl: PropTypes.string.isRequired,
  timeframe: PropTypes.string.isRequired,
}

export default CalendarDaysTable
